import { useEffect, useState } from "react";
import {
  getKBBehaviors,
  getZoneKBBehaviors,
  type EquipmentKBBehaviorsResponse,
  type KBBehavior,
  type KBBehaviorStatus,
  type OccupancyReadOut,
  type ZoneKBBehaviorsResponse,
} from "@/lib/api/agent";
import { cardTonePill } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { occupancyBadgeView } from "./occupancy-badge";
import { ZoneCapabilityBadge } from "./ZoneCapabilityBadge";
import { zoneCapabilityBadgeView } from "./zone-capability-badge";

const STATUS_DOT: Record<KBBehaviorStatus, string> = {
  active: "bg-success",
  fail: "bg-destructive",
  no_data: "bg-warning",
  unready: "bg-muted-foreground/50",
  unwired: "bg-muted-foreground/30",
};

const STATUS_TITLE: Record<KBBehaviorStatus, string> = {
  active: "Käynnissä — ei havaintoja",
  fail: "Havainto tehty",
  no_data: "Odottaa mittausdataa",
  unready: "Odottaa oppimista",
  unwired: "Signaali kytkemättä",
};

const STATUS_LABEL: Record<KBBehaviorStatus, string> = {
  active: "aktiivinen",
  fail: "havainto",
  no_data: "ei dataa",
  unready: "odottaa oppimista",
  unwired: "ei kytketty",
};

interface Props {
  buildingId: string;
  equipmentId: string;
}

export function KBBehaviorList({ buildingId, equipmentId }: Props) {
  const [data, setData] = useState<EquipmentKBBehaviorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsupported, setUnsupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setUnsupported(false);
    setError(null);
    getKBBehaviors(buildingId, equipmentId)
      .then(setData)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes("404") || msg.toLowerCase().includes("no kb")) {
          setUnsupported(true);
        } else {
          setError("Toimintatietojen lataus epäonnistui");
        }
      })
      .finally(() => setLoading(false));
  }, [buildingId, equipmentId]);

  if (loading) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-sm text-muted-foreground">Ladataan…</p>
      </div>
    );
  }

  if (unsupported) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-xs text-muted-foreground">
          Tämä laitetyyppi ei vielä sisälly tietopohjaan.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-xs text-destructive">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const activeCount = data.behaviors.filter(
    (b) => b.status === "active",
  ).length;
  const failCount = data.behaviors.filter((b) => b.status === "fail").length;
  // An unready behaviour's signals ARE wired — it waits on a learned/config parameter, not a
  // connection — so it must not be counted as "kytkemättä". Report it under its own label.
  const unwiredCount = data.behaviors.filter(
    (b) => b.status === "unwired",
  ).length;
  const unreadyCount = data.behaviors.filter(
    (b) => b.status === "unready",
  ).length;

  return (
    <div className="mt-4 space-y-1">
      <div className="flex items-center justify-between mb-2">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <span className="text-[10px] text-muted-foreground">
          {activeCount} aktiivista
          {failCount > 0 && (
            <span className="text-red-400"> · {failCount} havaintoa</span>
          )}
          {unwiredCount > 0 && ` · ${unwiredCount} kytkemättä`}
          {unreadyCount > 0 && ` · ${unreadyCount} odottaa oppimista`}
        </span>
      </div>

      {data.behaviors.map((behavior) => (
        <BehaviorRow
          key={behavior.id}
          behavior={behavior}
          expanded={expandedId === behavior.id}
          onToggle={() =>
            setExpandedId((prev) => (prev === behavior.id ? null : behavior.id))
          }
        />
      ))}
    </div>
  );
}

interface ZoneProps {
  buildingId: string;
  zoneId: string;
}

export function ZoneKBBehaviorList({ buildingId, zoneId }: ZoneProps) {
  const [data, setData] = useState<ZoneKBBehaviorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsupported, setUnsupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setUnsupported(false);
    setError(null);
    getZoneKBBehaviors(buildingId, zoneId)
      .then(setData)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (
          msg.includes("404") ||
          msg.includes("no type") ||
          msg.includes("No KB")
        ) {
          setUnsupported(true);
        } else {
          setError("Toimintatietojen lataus epäonnistui");
        }
      })
      .finally(() => setLoading(false));
  }, [buildingId, zoneId]);

  if (loading) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-sm text-muted-foreground">Ladataan…</p>
      </div>
    );
  }

  if (unsupported) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-xs text-muted-foreground">
          Tämä vyöhyketyyppi ei vielä sisälly tietopohjaan.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <p className="text-xs text-destructive">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const activeCount = data.behaviors.filter(
    (b) => b.status === "active",
  ).length;
  const failCount = data.behaviors.filter((b) => b.status === "fail").length;
  const unwiredCount = data.behaviors.filter(
    (b) => b.status === "unwired" || b.status === "no_data",
  ).length;

  return (
    <div className="mt-4 space-y-1">
      <div className="flex items-center justify-between mb-2">
        <SectionHeader>Odotettu toiminta</SectionHeader>
        <span className="text-[10px] text-muted-foreground">
          {activeCount} aktiivista
          {failCount > 0 && (
            <span className="text-red-400"> · {failCount} havaintoa</span>
          )}
          {unwiredCount > 0 && ` · ${unwiredCount} kytkemättä`}
        </span>
      </div>

      {data.occupancy && <OccupancyBadge occupancy={data.occupancy} />}

      {data.behaviors.map((behavior) => (
        <BehaviorRow
          key={behavior.id}
          behavior={behavior}
          expanded={expandedId === behavior.id}
          onToggle={() =>
            setExpandedId((prev) => (prev === behavior.id ? null : behavior.id))
          }
        />
      ))}
    </div>
  );
}

function BehaviorRow({
  behavior,
  expanded,
  onToggle,
}: {
  behavior: KBBehavior;
  expanded: boolean;
  onToggle: () => void;
}) {
  const dimmed = behavior.status === "unwired" || behavior.status === "unready";
  const capabilityView = behavior.capability
    ? zoneCapabilityBadgeView(behavior.capability)
    : null;

  return (
    <div
      className={`rounded-md transition-colors ${dimmed ? "opacity-50" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-2 px-2 py-1.5 text-left hover:bg-muted/30 rounded-md transition-colors"
      >
        {!behavior.capability && (
          <span
            className={`mt-1.5 shrink-0 h-2 w-2 rounded-full ${STATUS_DOT[behavior.status]}`}
            title={STATUS_TITLE[behavior.status]}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm">{behavior.name_fi}</span>
            {behavior.capability ? (
              <ZoneCapabilityBadge capability={behavior.capability} />
            ) : (
              behavior.status === "fail" && (
                <span className="shrink-0 rounded px-1 py-0.5 text-[9px] font-medium bg-red-500/10 text-red-400">
                  Havainto
                </span>
              )
            )}
          </div>
        </div>
        <span className="shrink-0 text-[9px] text-muted-foreground/50 mt-1">
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {expanded && (
        <div className="px-6 pb-2 space-y-2">
          {behavior.description_fi && (
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {behavior.description_fi}
            </p>
          )}

          {behavior.required_data_points.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Signaalit
              </p>
              <ul className="space-y-0.5">
                {behavior.required_data_points.map((dp) => (
                  <li
                    key={dp.id}
                    className="flex items-center gap-1.5 text-[11px]"
                  >
                    <span
                      className={
                        dp.wired
                          ? "text-success-subtle-foreground"
                          : "text-warning-subtle-foreground"
                      }
                    >
                      {dp.wired ? "✓" : "⚠"}
                    </span>
                    <span className="font-mono text-foreground">{dp.id}</span>
                    {!dp.wired && (
                      <span className="text-warning-subtle-foreground">
                        ei kytketty
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/50">
            {capabilityView
              ? `Vyöhykkeen signaali: ${capabilityView.zoneSignal.label} · Palveleva laite: ${capabilityView.equipmentBehavior.label}`
              : `Tila: ${STATUS_LABEL[behavior.status]}`}
            {behavior.full_potential_weight > 0 &&
              ` · painoarvo ${behavior.full_potential_weight}`}
          </p>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
      {children}
    </p>
  );
}

// Distinguishes a MEASURED occupancy (a dedicated, wired sensor) from an INFERRED one (resolved
// from the serving equipment's occupancy pattern + proxies), showing confidence for the inferred
// case and an honest "waiting" state on cold-start (story #496). Derivation is the testable
// `occupancyBadgeView`. Konfigurointi / Kyvykkyydet only — Sisäilmaolosuhteet tiles render
// occupancy as a live-signal row (or omit it), not this badge.
export function OccupancyBadge({ occupancy }: { occupancy: OccupancyReadOut }) {
  const { tone, label, detail, title } = occupancyBadgeView(occupancy);
  return (
    <div className="mb-2">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px]",
          cardTonePill[tone],
        )}
        title={title}
      >
        {label}
        {detail && <span className="text-muted-foreground">· {detail}</span>}
      </span>
    </div>
  );
}
